import React, { Children, ReactElement, useEffect, useState } from "react";
import { Persist } from "formik-persist";

import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  Radio,
  RadioGroup,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
} from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import {
  Field,
  FieldInputProps,
  Form,
  Formik,
  FormikConfig,
  FormikValues,
} from "formik";
import { PDFDownloadLink } from "@react-pdf/renderer";

import TextField from "../../../../components/Form/TextField";
import InputLabel from "../../../../components/Form/InputLabel";
import theme from "../../../../constants/theme";
import { BirthPlan, BirthPlanQuestion, FormikStepProps } from "./types";
import { getBirthPlanQuestions } from "../../../../api/birthplan";
import { blankBirthPlan } from "../../../../constants/objects";
import HospitalDropdownMenu from "../../../RodaHomepage/components/HospitalDropdownMenu";
import Button from "../../../../components/Button";
import BirthPlanPdf from "../BirthPlanPdf/BirthPlanPdf";

const useStyles = makeStyles(() =>
  createStyles({
    margin: {
      marginTop: theme.spacing(2),
    },
    subcategory: {
      marginTop: theme.spacing(2),
    },
    category: {
      cursor: "pointer",
      "&:hover": {
        cursor: "pointer",
      },
    },
    categories: {
      position: "sticky",
      top: theme.spacing(3),
      paddingLeft: 0,
      paddingTop: 0,
      background: "transparent",
    },
    stepContent: {
      margin: 0,
      padding: 0,
      paddingBottom: 10,
      [theme.breakpoints.down("sm")]: {
        marginRight: theme.spacing(1),
      },
    },
    subcategoryStyle: {
      marginBottom: theme.spacing(2),
      marginTop: theme.spacing(2),
    },
    downloadPdf: {
      color: "#fff",
      textDecoration: "none",
      cursor: "pointer",
    },
    stepper: {
      boxShadow: "rgba(0, 0, 0, 0.15) 0px 5px 15px 0px;",
      padding: theme.spacing(4),
      marginRight: theme.spacing(6),
      [theme.breakpoints.down("sm")]: {
        padding: 0,
        marginRight: 0,
        boxShadow: "none",
      },
    },
  })
);

export function createField(
  field: FieldInputProps<any>,
  form: any,
  question: BirthPlanQuestion,
  fieldName: string,
  hospitalName: string,
  setHospitalName: React.Dispatch<React.SetStateAction<string>>
) {
  const radioGroupStyle = { marginBottom: theme.spacing(2) };
  const textFieldMargin = {
    marginTop: theme.spacing(0.5),
  };
  const inputLabelMargin = {
    marginTop: theme.spacing(1),
  };

  switch (question.type) {
    case "TEXT":
      return (
        <>
          <InputLabel
            key={question.label}
            style={question.dependsOn.length ? inputLabelMargin : {}}
          >
            {question.label}
          </InputLabel>
          <TextField
            value={field.value}
            name={field.name}
            fullWidth
            onChange={(event) =>
              form.setFieldValue(field.name, event.target.value)
            }
          />
        </>
      );
    case "DATE":
      return (
        <>
          <InputLabel key={question.label}>{question.label}</InputLabel>
          <Field name={field.name} type="date" fullWidth as={TextField} />
        </>
      );
    case "RADIO":
      return (
        <>
          <InputLabel key={question.label}>{question.label}</InputLabel>
          <RadioGroup
            name={field.name}
            value={field.value}
            style={radioGroupStyle}
          >
            <>
              {question.offeredOptions?.map((option) => (
                <FormControlLabel
                  name={field.name}
                  key={option.name}
                  id={option.name}
                  value={option.label}
                  control={<Radio />}
                  label={option.label}
                  onChange={() => {
                    form.setFieldValue(field.name, option.label);
                  }}
                  style={textFieldMargin}
                />
              ))}
            </>
          </RadioGroup>
        </>
      );
    case "DROPDOWN":
      return (
        <>
          <InputLabel
            key={question.label}
            style={{ marginTop: 20, marginBottom: 20 }}
          >
            {question.label}
          </InputLabel>
          <HospitalDropdownMenu
            setFieldValue={form.setFieldValue}
            fieldName={field.name}
            hospitalName={
              form.values.plan.categories[1].subcategories[0].questions[1]
                .value || hospitalName
            }
            setHospitalName={setHospitalName}
            isBirthPlan={true}
          />
        </>
      );
    case "CHECKBOX":
      return (
        <FormControl component="fieldset" fullWidth>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  onChange={(event) => {
                    form.setFieldValue(field.name, event.target.checked);
                    field.onChange(event);
                  }}
                  checked={field.value}
                />
              }
              label={question.label}
              style={textFieldMargin}
            />
          </FormGroup>
        </FormControl>
      );
    default:
      break;
  }
}

export default function BirthPlanForm() {
  const classes = useStyles();
  const [plan, setPlan] = useState<BirthPlan>(blankBirthPlan);
  const [hospitalName, setHospitalName] = useState<string>("");
  let fieldName: string = "";

  useEffect(() => {
    async function fetchData() {
      const response = await getBirthPlanQuestions();
      const birthPlan: BirthPlan = response.data;
      birthPlan.categories.forEach((category) =>
        category.subcategories.forEach((subcategory) =>
          subcategory.questions.forEach((question) => {
            question.value = "";
            question.dependsOn
              ?.filter(
                (condition) =>
                  condition.conditionalValue == "true" ||
                  condition.conditionalValue == "false"
              )
              .map((condition) =>
                condition.conditionalValue === "true"
                  ? (condition.conditionalValue = true)
                  : (condition.conditionalValue = false)
              );
          })
        )
      );
      setPlan(birthPlan);
    }

    if (!localStorage.getItem("birth-plan")) {
      fetchData();
    } else {
      let localStorageObject = JSON.parse(
        localStorage.getItem("birth-plan") as string
      );
      let formData = localStorageObject["values"]["plan"];
      setPlan(formData);
    }
  }, []);

  function checkCondition(question: BirthPlanQuestion, form: any): boolean {
    return question.dependsOn.length
      ? question.dependsOn.some(
          (condition) =>
            form.values.plan.categories[condition.categoryId - 1].subcategories[
              condition.subcategoryIdxInCategory
            ].questions[condition.questionIdxInSubcategory].value ==
            condition.conditionalValue
        )
      : true;
  }

  return plan.categories.length == 9 ? (
    <FormikStepper initialValues={{ plan }} onSubmit={() => {}}>
      {plan.categories.map((category, idxCategory) => (
        <>
          {!category.subcategories.length && (
            <>
              <Typography className={classes.margin}>
                {category.categoryDescription}
              </Typography>
            </>
          )}
          {category.subcategories.map((subcategory, idxSubcategory) => (
            <>
              <FormikStep label={category.category} key={category.category}>
                {subcategory.subcategory == null && (
                  <>
                    <Typography className={classes.margin}>
                      {category.categoryDescription}
                    </Typography>
                  </>
                )}

                {subcategory.subcategory && (
                  <>
                    <Typography
                      variant="body2"
                      color="secondary"
                      className={classes.subcategory}
                    >
                      {subcategory.subcategory}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      className={classes.subcategoryStyle}
                    >
                      {subcategory.subcategoryDescription}
                    </Typography>
                  </>
                )}
                {subcategory.questions.map((question, idxQuestion) => (
                  <>
                    {
                      <div style={{ display: "none" }}>
                        {
                          (fieldName = `plan.categories[${idxCategory}].subcategories[${idxSubcategory}].questions[${idxQuestion}].value`)
                        }
                      </div>
                    }
                    <Field name={fieldName} key={"q" + question.id} fullWidth>
                      {({
                        field,
                        form,
                      }: {
                        field: FieldInputProps<any>;
                        form: any;
                      }) => (
                        <>
                          {question.name == "hospital" &&
                            question.dependsOn
                              .filter(
                                (condition) =>
                                  condition.questionName == "caesarian-planned"
                              )
                              .every(
                                (condition) =>
                                  form.values.plan.categories[
                                    condition.categoryId - 1
                                  ].subcategories[
                                    condition.subcategoryIdxInCategory
                                  ].questions[
                                    condition.questionIdxInSubcategory
                                  ].value == condition.conditionalValue
                              ) && (
                              <Typography variant={"subtitle2"}>
                                Ranije ste odabrali da planirate roditi carskim
                                rezom, stoga druge opcije za mjesto rađanja nisu
                                ponuđene.
                              </Typography>
                            )}
                          {checkCondition(question, form) &&
                            createField(
                              field,
                              form,
                              question,
                              field.name,
                              hospitalName,
                              setHospitalName
                            )}
                        </>
                      )}
                    </Field>
                  </>
                ))}
              </FormikStep>
            </>
          ))}
        </>
      ))}
    </FormikStepper>
  ) : (
    <></>
  );
}

export function FormikStep({ children }: FormikStepProps) {
  return <>{children}</>;
}

export function FormikStepper({
  children,
  ...props
}: FormikConfig<FormikValues>) {
  const steps = Children.toArray(children) as ReactElement<FormikStepProps>[];
  const classes = useStyles();
  const [activeStep, setActiveStep] = useState(0);
  const currentChild = steps[activeStep];
  const [completed, setCompleted] = useState(false);

  function isLastStep() {
    return activeStep === steps.length - 1;
  }

  useEffect(() => {
    if (activeStep === steps.length - 1) {
      setCompleted(true);
    } else {
      setCompleted(false);
    }
  }, [activeStep, steps.length]);

  return (
    <Formik
      {...props}
      validationSchema={currentChild.props.validationSchema}
      onSubmit={async () => {}}
    >
      {({ values }) =>
        steps.length == 9 ? (
          <Form>
            <Grid container xs={12} md={12}>
              <div className={classes.stepper}>
                <Stepper
                  activeStep={activeStep}
                  orientation="vertical"
                  className={classes.categories}
                >
                  {steps.map((child, index) => (
                    <Step
                      key={child.props.label}
                      completed={activeStep > index || completed}
                    >
                      <StepLabel
                        onClick={() => setActiveStep(index)}
                        className={classes.category}
                      >
                        {values.plan.categories[index].category}
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </div>

              <Grid
                container
                xs={12}
                md={6}
                lg={7}
                justifyContent="space-between"
              >
                <Stepper
                  activeStep={activeStep}
                  orientation="vertical"
                  className={classes.stepContent}
                >
                  <Step expanded={true}>
                    <Typography variant="h4">
                      {values.plan.categories[activeStep].category}
                    </Typography>
                    <StepContent className={classes.stepContent}>
                      {currentChild}
                    </StepContent>
                  </Step>
                </Stepper>

                <Grid
                  container
                  spacing={2}
                  justifyContent="flex-end"
                  alignItems="flex-end"
                >
                  {activeStep > 0 ? (
                    <Grid item>
                      <Button
                        onClick={() => {
                          setActiveStep((s) => s - 1);
                          window.scrollTo(0, 0);
                        }}
                      >
                        Natrag
                      </Button>
                    </Grid>
                  ) : null}
                  <Grid item>
                    {isLastStep() ? (
                      <PDFDownloadLink
                        document={<BirthPlanPdf plan={values.plan} />}
                        fileName="Moj_plan_poroda.pdf"
                        className={classes.downloadPdf}
                      >
                        <Button>
                        Preuzmi PDF
                        </Button>
                      </PDFDownloadLink>
                    ) : (
                      <Button
                        onClick={() => {
                          setActiveStep((s) => s + 1);
                          window.scrollTo(0, 0);
                        }}
                      >
                      Dalje
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Persist name={"birth-plan"} />
          </Form>
        ) : (
          <></>
        )
      }
    </Formik>
  );
}
